import type { PropertyFilterOperatorExtended } from '@cloudscape-design/collection-hooks';
import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { getDateRangeFormat, toLocalDate } from '@/utils/dateUtils';

import { convertDateRangeValues } from '../../pages/dashboards/widgets/filterHelpers';
import { DateFilterForm } from './DateFilterForm';
import type { DateFilterValue } from './dateFilterValue';
import { isDateThresholdValue, matchDateThreshold } from './dateFilterValue';
import { RelativeDateTimeForm } from './RelativeDateTimeForm';

const matchDateRange = (itemValue: unknown, tokenValue: null | string) => {
  const tItemValue = itemValue as string;
  const tTokenValue =
    (tokenValue as unknown as DateRangePickerProps.Value) || null;

  const token = convertDateRangeValues(tTokenValue);
  const itemDate = dayjs(tItemValue);

  const isSameDay =
    itemDate.isSame(token.startDate, 'day') ||
    itemDate.isSame(token.endDate, 'day');

  if (isSameDay) {
    return true;
  }

  const isAfterStartDate = itemDate.isAfter(token.startDate, 'day');
  const isBeforeEndDate = itemDate.isBefore(token.endDate, 'day');

  return isAfterStartDate && isBeforeEndDate;
};

/**
 * Range-only date operator. Feeds the form conditional-logic builder, whose rule
 * evaluator understands equality only — a threshold condition there would be
 * accepted and then silently never fire.
 */
export const dateRangeFilterOperators: PropertyFilterOperatorExtended<
  null | string
>[] = [
  {
    operator: '=',
    form: RelativeDateTimeForm,
    format: getDateRangeFormat,
    match: matchDateRange,
  },
];

/**
 * Date operators for tabular filtering — registers, settings tables and the smart
 * widget. Deliberately ONE operator (CUS-217): Cloudscape auto-inserts the
 * operator only when a property has exactly one, so the inclusive comparisons are
 * offered inside `DateFilterForm` instead of as `>=` / `<=`. Adding operators cost
 * a click on every date filter and hid the range picker behind "Equals".
 */
export const dateTableFilterOperators: PropertyFilterOperatorExtended<
  null | string
>[] = [
  {
    operator: '=',
    form: DateFilterForm,
    format: (value) => {
      const tValue = value as unknown as DateFilterValue | null;
      if (isDateThresholdValue(tValue)) {
        return t(
          `dateFilter.${tValue.mode === 'onOrAfter' ? 'onOrAfterFormat' : 'onOrBeforeFormat'}`,
          { date: toLocalDate(tValue.date) }
        );
      }

      return getDateRangeFormat(value);
    },
    match: (itemValue, tokenValue: null | string) => {
      const tValue = tokenValue as unknown as DateFilterValue | null;
      if (isDateThresholdValue(tValue)) {
        return matchDateThreshold(itemValue, tValue);
      }

      return matchDateRange(itemValue, tokenValue);
    },
  },
];
